/**
 * Converte uma cor hexadecimal para HSL
 */
const hexToHsl = (hex: string): { h: number; s: number; l: number } => {
  try {
    const cleanHex = hex.replace('#', '');

    if (!/^[0-9A-F]{6}$/i.test(cleanHex)) {
      throw new Error(`Invalid hex color: ${hex}`);
    }

    const r = parseInt(cleanHex.substr(0, 2), 16) / 255;
    const g = parseInt(cleanHex.substr(2, 2), 16) / 255;
    const b = parseInt(cleanHex.substr(4, 2), 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }
      h /= 6;
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100),
    };
  } catch (error) {
    return { h: 25, s: 100, l: 50 };
  }
};

/**
 * Converte HSL para hexadecimal
 */
const hslToHex = (h: number, s: number, l: number): string => {
  h = h / 360;
  s = s / 100;
  l = l / 100;

  const hue2rgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r: number, g: number, b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (c: number): string => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

/**
 * Gera um array de cores únicas baseado em uma cor base
 * As cores são variações do espectro da cor base usando HSL
 *
 * @param baseColor - Cor base em formato hexadecimal (ex: '#FF8211')
 * @param count - Quantidade de cores a serem geradas
 * @returns Array de cores em formato hexadecimal
 */
export const generateChartColors = (
  baseColor = '#FF8211',
  count: number,
): string[] => {
  if (count <= 0) return [];
  if (count === 1) return [baseColor];

  const colors: string[] = [];

  colors.push(baseColor);

  for (let i = 1; i < count; i++) {
    const paletteIndex = (i - 1) % (CHART_COLORS_PALETTE.length - 1);
    colors.push(CHART_COLORS_PALETTE[paletteIndex + 1]); // +1 para pular a primeira (que é a base)
  }

  return colors;
};

/**
 * Paleta de cores pré-definida para gráficos
 * Útil quando você quer cores específicas e consistentes
 */
export const CHART_COLORS_PALETTE = [
  '#FF8211', // Laranja base
  '#BF360C', // Vermelho muito escuro
  '#FF9A56', // Laranja mais claro
  '#E65100', // Laranja escuro
  '#FFB366', // Laranja claro
  '#FF6B35', // Laranja mais escuro
  '#FFAB40', // Laranja médio
  '#D94A1F', // Vermelho escuro
  '#FFCC80', // Laranja muito claro
  '#E55A2B', // Vermelho-laranja
];
